import React, { useEffect, useRef, useState } from "react";
import { GoogleAuthProvider, signInWithPopup, RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { toast } from "sonner";
import { Loader2, Phone, ArrowLeft, ShieldCheck } from "lucide-react";
import { auth } from "@/lib/firebase";
import { useAuth } from "@/context/AuthContext";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

const COUNTRY_CODES = [
  { code: "+91", label: "🇮🇳 +91" },
  { code: "+1", label: "🇺🇸 +1" },
  { code: "+44", label: "🇬🇧 +44" },
  { code: "+61", label: "🇦🇺 +61" },
  { code: "+971", label: "🇦🇪 +971" },
  { code: "+65", label: "🇸🇬 +65" },
];

const friendlyFbError = (err) => {
  const code = err?.code || "";
  const msg = (err?.message || "").toLowerCase();
  if (msg.includes("region")) {
    return "SMS to this country is blocked in your Firebase SMS Region Policy. Enable the region in Firebase → Authentication → Settings.";
  }
  const map = {
    "auth/invalid-phone-number": "That phone number looks invalid. Include a valid mobile number.",
    "auth/missing-phone-number": "Please enter your phone number.",
    "auth/too-many-requests": "Too many attempts. Please wait a few minutes and try again.",
    "auth/invalid-verification-code": "That code isn't correct. Please check and try again.",
    "auth/code-expired": "That code expired. Please request a new one.",
    "auth/popup-closed-by-user": "Sign-in was cancelled.",
    "auth/cancelled-popup-request": "Sign-in was cancelled.",
    "auth/popup-blocked": "Your browser blocked the sign-in popup. Please allow popups and retry.",
    "auth/network-request-failed": "Network error. Check your connection and try again.",
    "auth/billing-not-enabled": "Phone sign-in isn't available right now. Try Google sign-in.",
    "auth/operation-not-allowed": "This sign-in method isn't enabled. Try Google sign-in.",
    "auth/unauthorized-domain": "This domain isn't authorized for sign-in yet.",
    "auth/internal-error": "Phone sign-in failed — check that this domain is authorized and phone billing (Blaze) is enabled.",
    "auth/captcha-check-failed": "reCAPTCHA check failed. Reload the page and try again.",
    "auth/quota-exceeded": "SMS limit reached for now. Please try again later or use Google sign-in.",
    "auth/missing-app-credential": "Phone verification setup issue (reCAPTCHA). Reload and try again.",
  };
  return map[code] || `Something went wrong${code ? ` (${code})` : ""}. Please try again.`;
};

export const AuthPanel = ({ onSuccess, recaptchaId = "sf-recaptcha-container", compact = false }) => {
  const { loginWithIdToken } = useAuth();
  const [step, setStep] = useState("enter-phone"); // enter-phone | enter-otp
  const [cc, setCc] = useState("+91");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const verifierRef = useRef(null);
  const confirmationRef = useRef(null);

  useEffect(() => {
    return () => {
      try { verifierRef.current?.clear?.(); } catch { /* noop */ }
      verifierRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const finish = async (fbUser) => {
    const idToken = await fbUser.getIdToken();
    const u = await loginWithIdToken(idToken);
    toast.success(`Welcome${u?.name ? `, ${u.name.split(" ")[0]}` : ""}!`);
    onSuccess?.(u);
  };

  const handleGoogle = async () => {
    if (googleBusy) return;
    setGoogleBusy(true);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      await finish(result.user);
    } catch (err) {
      console.warn("google signin error", err);
      if (err?.code !== "auth/popup-closed-by-user" && err?.code !== "auth/cancelled-popup-request") {
        toast.error(friendlyFbError(err));
      }
    } finally {
      setGoogleBusy(false);
    }
  };

  const getVerifier = () => {
    if (!verifierRef.current) {
      verifierRef.current = new RecaptchaVerifier(auth, recaptchaId, { size: "invisible" });
    }
    return verifierRef.current;
  };

  const sendOtp = async () => {
    if (busy) return;
    const digits = phone.replace(/[^0-9]/g, "");
    if (digits.length < 6) { toast.error("Please enter a valid phone number."); return; }
    setBusy(true);
    try {
      const verifier = getVerifier();
      const full = `${cc}${digits}`;
      confirmationRef.current = await signInWithPhoneNumber(auth, full, verifier);
      setStep("enter-otp");
      setOtp("");
      setResendIn(30);
      toast.success(`Code sent to ${full}`);
    } catch (err) {
      console.warn("send otp error", err);
      toast.error(friendlyFbError(err));
      try { verifierRef.current?.clear?.(); } catch { /* noop */ }
      verifierRef.current = null;
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (code) => {
    const value = code ?? otp;
    if (busy) return;
    if (!confirmationRef.current) { toast.error("Please request a code first."); return; }
    if (value.length !== 6) return;
    setBusy(true);
    try {
      const result = await confirmationRef.current.confirm(value);
      await finish(result.user);
    } catch (err) {
      console.warn("verify otp error", err);
      toast.error(friendlyFbError(err));
      setOtp("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="auth-panel">
      {/* Google */}
      <button
        type="button"
        onClick={handleGoogle}
        disabled={googleBusy}
        data-testid="auth-google-btn"
        className="w-full inline-flex items-center justify-center gap-3 rounded-full border border-black/10 bg-white text-ink font-medium h-12 transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60"
      >
        {googleBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : (
          <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09Z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.98.66-2.24 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/>
            <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84Z"/>
            <path fill="#EA4335" d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.46 14.97.5 12 .5A11 11 0 0 0 2.18 7.05l3.66 2.84C6.71 6.68 9.14 4.75 12 4.75Z"/>
          </svg>
        )}
        Continue with Google
      </button>

      <div className="flex items-center gap-3 my-5">
        <div className="h-px flex-1 bg-black/10" />
        <span className="text-xs font-medium text-ink-faint">or continue with phone</span>
        <div className="h-px flex-1 bg-black/10" />
      </div>

      {step === "enter-phone" && (
        <div className="space-y-3" data-testid="auth-phone-step">
          <label className="block">
            <span className="text-sm font-medium text-ink">Phone number</span>
            <div className="mt-1.5 flex gap-2">
              <select
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                data-testid="auth-country-code"
                className="rounded-xl border border-black/10 bg-white px-2 text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
              >
                {COUNTRY_CODES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
              </select>
              <input
                type="tel"
                inputMode="numeric"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") sendOtp(); }}
                placeholder="98765 43210"
                data-testid="auth-phone-input"
                className="flex-1 min-w-0 rounded-xl border border-black/10 bg-white px-4 py-2.5 text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
              />
            </div>
          </label>
          <button
            type="button"
            onClick={sendOtp}
            disabled={busy}
            data-testid="auth-send-otp-btn"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white font-semibold h-12 transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
            Send code
          </button>
        </div>
      )}

      {step === "enter-otp" && (
        <div className="space-y-4" data-testid="auth-otp-step">
          <button
            type="button"
            onClick={() => { setStep("enter-phone"); confirmationRef.current = null; }}
            data-testid="auth-otp-back"
            className="inline-flex items-center gap-1.5 text-sm text-ink-soft hover:text-brand transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Change number
          </button>
          <div>
            <p className="text-sm font-medium text-ink">Enter the 6-digit code</p>
            <p className="text-xs text-ink-soft mt-0.5">Sent to {cc} {phone}</p>
          </div>
          <InputOTP
            maxLength={6}
            value={otp}
            onChange={(v) => { setOtp(v); if (v.length === 6) verifyOtp(v); }}
            data-testid="auth-otp-input"
          >
            <InputOTPGroup className="gap-2">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <InputOTPSlot key={i} index={i} className="h-12 w-11 rounded-lg border-black/15 text-lg" />
              ))}
            </InputOTPGroup>
          </InputOTP>
          <button
            type="button"
            onClick={() => verifyOtp()}
            disabled={busy || otp.length !== 6}
            data-testid="auth-verify-otp-btn"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white font-semibold h-12 transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Verify &amp; continue
          </button>
          <button
            type="button"
            onClick={sendOtp}
            disabled={resendIn > 0 || busy}
            data-testid="auth-resend-otp"
            className="w-full text-center text-sm text-ink-soft hover:text-brand transition-colors disabled:opacity-50"
          >
            {resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}
          </button>
        </div>
      )}

      {!compact && (
        <p className="mt-6 flex items-center justify-center gap-1.5 text-xs text-ink-faint">
          <ShieldCheck className="w-3.5 h-3.5 text-brand" /> Your details are secure. No spam, ever.
        </p>
      )}

      {/* invisible reCAPTCHA target for phone auth */}
      <div id={recaptchaId} />
    </div>
  );
};

export default AuthPanel;
